import { useEffect, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import { Tooltip, Typography } from '@mui/material';
import { Constants } from '../../core/constants';
import { Gear, KratosTable, Mnemos, Titan, Weapon } from '../../core/types';
import { Utils } from '../../core/utilities';
import SearchableSelect from '../../components/SearchableSelect';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import Button from '../../components/Button';

interface SpoilerConfigSectionProps {
    titans: Titan[];
    kratosTables: KratosTable[];
    weapons: Weapon[];
    gear: Gear[];
    mnemos: Mnemos[];
    visibleTags: string[];
    setVisibleTags: React.Dispatch<React.SetStateAction<string[]>>;
}

export const SpoilerConfigSection: React.FC<SpoilerConfigSectionProps> = ({ titans, kratosTables, weapons, gear, mnemos, visibleTags, setVisibleTags }) => {
    const isCompact = useCompactLayout();
    const [isExpanded, setIsExpanded] = useState(() => {
        const storedExpanded = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem(Constants.LocalStorageKeys.SPOILER_SETTINGS_EXPANDED) || 'null') : null;
        return typeof storedExpanded === 'boolean' ? storedExpanded : !isCompact;
    });

    useEffect(() => {
        localStorage.setItem(Constants.LocalStorageKeys.SPOILER_SETTINGS_EXPANDED, JSON.stringify(isExpanded));
    }, [isExpanded]);

    const getTags = () => {
        return Utils.getUniqueTags([...titans, ...kratosTables, ...weapons, ...gear, ...mnemos, ...Object.values(Constants.SpoilerTag).map((t) => ({ tags: [t] }))]);
    };

    const handleAddTag = (tag: string) => {
        setVisibleTags(Utils.expandCycleSpoilerTags([...visibleTags, tag]));
    };

    const handleRemoveTag = (tag: string) => {
        const updatedTags = visibleTags.filter((visibleTag) => visibleTag !== tag) ?? [];
        setVisibleTags(updatedTags);
    };

    return (
        <div className="input-section">
            <div className="panel-section-header">
                <div className="panel-section-heading">
                    <h3>Spoiler Settings</h3>
                    {isCompact && <p className="panel-section-summary">{visibleTags.length} visible</p>}
                </div>
                <Button className="panel-collapse-button" variant="ghost" size="sm" aria-expanded={isExpanded} onClick={() => setIsExpanded((current) => !current)}>
                    {isExpanded ? 'Hide' : 'Show'}
                </Button>
            </div>
            <div className={`panel-section-body ${!isExpanded ? 'panel-section-body-collapsed' : ''}`}>
                <div className="loadout-select-row">
                    <Tooltip
                        title={
                            <pre className="tooltip-pre">
                                <strong>Use this to reveal spoiler-tagged content across the app.</strong>
                                <br />
                                Add tags to show matching items, remove tags below to hide them again, or use <strong>Reveal All</strong> and <strong>Reset Tags</strong> for quick shortcuts.
                            </pre>
                        }
                        arrow
                        enterDelay={500}
                        enterNextDelay={500}
                    >
                        <Typography component="label" className="loadout-select-label input-label-with-tooltip">
                            Reveal Tags
                            <InfoIcon className="info-icon-small" />
                        </Typography>
                    </Tooltip>
                    <div className="loadout-input-group">
                        <SearchableSelect
                            value=""
                            options={getTags().map((tag) => ({ value: tag }))}
                            onChange={handleAddTag}
                            placeholder="Add a tag"
                            ariaLabel="Spoiler Tags"
                            clearOnSelect
                        />
                    </div>
                </div>
                <div className="loadout-row">
                    <ul className="selected-items">
                        {visibleTags.sort().map((tag) => (
                            <li key={tag}>
                                <Button type="button" className="remove-item" variant="ghost" size="sm" aria-label={`Remove ${tag}`} onClick={() => handleRemoveTag(tag)}>
                                    x
                                </Button>
                                {tag}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="spacer" />
                <div className="input-section-button-container">
                    <Button variant="primary" fullWidth onClick={() => setVisibleTags(getTags())}>
                        Reveal All
                    </Button>
                    <Button variant="danger" fullWidth onClick={() => setVisibleTags(Constants.DEFAULT_VISIBLE_SPOILERS)}>
                        Reset Tags
                    </Button>
                </div>
            </div>
        </div>
    );
};
